import React, { useEffect, useState } from 'react';
import { Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Alert, Box, Stack, Divider } from '@mui/material';
import { Block, Cancel, CheckCircle, Warning } from '@mui/icons-material';
import { Payment, PaymentConcept } from '@/types/payment.types';
import TableList, { TableColumn } from '@/components/common/TableList';
import { useAppDispatch } from '@/app/hooks';
import { fetchFilteredPurchases } from '@/features/payments/paymentsThunks';
import { useSelector } from 'react-redux';
import {  selectPagination, selectPaymentFilters, selectPayments, selectPaymentsError, selectPaymentsLoading } from '@/features/payments/paymentsSelectors';
import { setPageSize } from '../../../../features/payments/paymentsSlice';
import { ExportPaymentsButton } from './ExportPaymentsButton';
import LoadingOverlay from '@/components/common/LoadingOverlay';


const PaymentsList: React.FC = () => {
    const dispatch = useAppDispatch();
    const payments = useSelector(selectPayments)
    const isLoading = useSelector(selectPaymentsLoading);
    const error = useSelector(selectPaymentsError);
    const pagination = useSelector(selectPagination); 
    const filters = useSelector(selectPaymentFilters);
    const [page, setPage] = useState(0);

    const [openModal, setOpenModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState<Payment | null>(null);


    useEffect(() => {
        setPage(pagination.currentPage - 1);
    }, [pagination.currentPage]);

    useEffect(() => {
        // Initial load with pagination
        if (payments.length === 0 && !isLoading) {
            dispatch(
                fetchFilteredPurchases({
                    filters,
                    pageSize: pagination.pageSize,
                })
            );
        }
    }, [dispatch]);


    const handlePageChange = (newPage: number) => {
        const direction = newPage > page ? 'next' : 'previous';
  
        dispatch(
          fetchFilteredPurchases({
            filters,
            pageSize: pagination.pageSize,
            direction,
            lastVisible: direction === 'next' ? pagination.lastVisible : undefined,
            firstVisible: direction === 'previous' ? pagination.firstVisible : undefined,
          })
        );
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        // Reset pagination state and page size
        dispatch(setPageSize(newRowsPerPage));

        // Fetch from the first page with new page size
        dispatch(
          fetchFilteredPurchases({
            filters,
            pageSize: newRowsPerPage,
          })
        );
    };

    const handleOpenModal = (payment: Payment) => {
        setSelectedPurchase(payment);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedPurchase(null);
    };

    const formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const getEstadoColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'completed':
            case 'approved':
                return 'success';
            case 'failed':
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const getEstadoIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Warning fontSize="small" />;
            case 'completed':
            case 'approved':
                return <CheckCircle fontSize="small" />;
            case 'failed':
            case 'rejected':
                return <Cancel fontSize="small" />;
            default:
                return <Block fontSize="small" />;
        }
    };

    const getEstadoLabel = (status: string): string => {
        switch (status) {
            case 'pending':
                return 'Pendiente';
            case 'completed':
                return 'Completado';
            case 'approved':
                return 'Aprobado';
            case 'failed':
                return 'Fallido';
            case 'rejected':
                return 'Rechazado';
            default:
                return status;
        }
    };

    const columns: TableColumn<Payment>[] = [
        {
            key: 'createdAt',
            label: 'Fecha y Hora',
            render: (payment) => {
                const date = new Date(payment.createdAt);
                const dateStr = date.toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
                const timeStr = date.toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return `${dateStr} ${timeStr}`;
            }
        },
        {
            key: 'description',
            label: 'Detalles',
            render: (payment) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenModal(payment)}
                    sx={{
                        textTransform: 'none',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                >
                    Ver
                </Button>
            ),
        },
        {
            key: 'customerEmail',
            label: 'Correo',
            hideOn: { xs: true },
        },
        {
            key: 'site',
            label: 'Sede',
            hideOn: { xs: true },
            render: (payment) => payment.site || 'N/A',
        },
        {
            key: 'referenceCode',
            label: 'Referencia',
            hideOn: { xs: true },
        },
        {
            key: 'status',
            label: 'Estado',
            render: (payment) => (
                <Chip
                    icon={getEstadoIcon(payment.status)}
                    label={getEstadoLabel(payment.status)}
                    color={getEstadoColor(payment.status)}
                    size="small"
                    sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: 24, sm: 28 }
                    }}
                />
            ),
        },
        {
            key: 'amount',
            label: 'Total',
            align: 'right',
            render: (payment) => payment.amount ? `$${payment.amount.toString()}` : '',
            cellSx: {
                fontWeight: 600,
                fontSize: { xs: '0.85rem', sm: '0.875rem' }
            }
        },
    ];

    // Calculate count for pagination: if we have a next page, use -1 (unknown), otherwise use actual count
    const paginationCount = pagination.hasNextPage
        ? -1
        : page * pagination.pageSize + payments.length;

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Error al cargar los pagos: {error}
            </Alert>
        );
    }


    return (
        <>
            {isLoading && <LoadingOverlay message="Cargando pagos..." />}
            <TableList
                data={payments}
                columns={columns}
                title="Historial"
                getRowKey={(payment) => payment.id}
                enablePagination={true}
                page={page}
                rowsPerPage={pagination.pageSize}
                totalCount={paginationCount}
                hasNextPage={pagination.hasNextPage}
                hasPreviousPage={pagination.hasPreviousPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                footer={<ExportPaymentsButton/>}
            />

            {/* Payment Details Modal */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
                    Detalles del Pago
                </DialogTitle>
                <DialogContent>
                    {selectedPurchase && (
                        <Box sx={{ pt: 2 }}>
                            <Stack spacing={3}>
                                {/* Header with Status */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    spacing={2}
                                >
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Referencia
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>
                                            {selectedPurchase.referenceCode}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        icon={getEstadoIcon(selectedPurchase.status)}
                                        label={getEstadoLabel(selectedPurchase.status)}
                                        color={getEstadoColor(selectedPurchase.status)}
                                        size="medium"
                                    />
                                </Stack>

                                <Divider />

                                {/* Payment Information */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                                        gap: 3,
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Fecha de pago
                                            </Typography>
                                            <Typography variant="body2">
                                                {new Date(selectedPurchase.createdAt).toLocaleDateString('es-ES', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Monto total
                                            </Typography>
                                            <Typography variant="h6" color="primary" fontWeight={600}>
                                                {formatCurrency(selectedPurchase.amount)}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Cliente
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedPurchase.customerName}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Correo electrónico
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedPurchase.customerEmail}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>

                                {/* Concepts Section */}
                                {selectedPurchase.concepts && selectedPurchase.concepts.length > 0 && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                Conceptos de pago
                                            </Typography>
                                            <Stack spacing={1.5} sx={{ mt: 2 }}>
                                                {selectedPurchase.concepts.map((concept: PaymentConcept, index: number) => (
                                                    <Box
                                                        key={`${concept.id}-${index}`}
                                                        sx={{
                                                            p: 2,
                                                            bgcolor: '#F5F5F5',
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Box>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {concept.name}
                                                                </Typography>
                                                                {concept.studentId && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        ID Estudiante: {concept.studentId}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {formatCurrency(concept.amount)}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </>
                                )}

                                {/* Description */}
                                {selectedPurchase.description && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Descripción
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {selectedPurchase.description}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseModal}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            px: 3
                        }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PaymentsList;